<template>
  <div class="container">
    <!-- Product betöltődött vagy skeleton, mert még tölt -->
    <div v-if="product !== null || isProductLoading" class="my-5">
      <!-- Meta adatok -->
      <div
        v-if="product !== null && product.isVisible"
        itemscope
        itemtype="https://schema.org/Product"
      >
        <meta
          v-if="product.metaData.product.sku !== null"
          itemprop="sku"
          :content="product.metaData.product.sku"
        >
        <meta
          v-if="product.metaData.product.gtin !== ''"
          itemprop="gtin"
          :content="product.metaData.product.gtin"
        >
        <meta itemprop="category" :content="product.metaData.product.category">
        <meta itemprop="name" :content="product.metaData.product.name">
        <meta itemprop="description" :content="product.metaData.product.description">
        <meta itemprop="image" :content="product.metaData.product.image">
        <span itemprop="offers" itemscope itemtype="https://schema.org/Offer">
          <meta itemprop="url" :content="product.metaData.product.offers.url">
          <meta itemprop="priceCurrency" :content="product.metaData.product.offers.priceCurrency">
          <meta itemprop="price" :content="product.metaData.product.offers.price">
          <meta itemprop="priceValidUntil" :content="product.metaData.product.offers.priceValidUntil">
          <link itemprop="availability" :href="product.metaData.product.offers.availability">
          <link itemprop="itemCondition" href="https://schema.org/NewCondition">
        </span>
        <span itemprop="brand" itemscope itemtype="https://schema.org/Brand">
          <meta itemprop="logo" :content="product.metaData.product.brand.logo">
          <meta itemprop="name" :content="product.metaData.product.brand.name">
          <meta itemprop="url" :content="product.metaData.product.brand.url">
        </span>
        <template v-if="product && !product.isAdsFreeProduct">
          <template v-if="'aggregateRating' in product.metaData.product">
            <div
              itemprop="aggregateRating"
              itemscope
              itemtype="https://schema.org/AggregateRating"
            >
              <meta itemprop="ratingValue" :content="product.metaData.product.aggregateRating.ratingValue">
              <meta itemprop="reviewCount" :content="product.metaData.product.aggregateRating.reviewCount">
              <meta itemprop="bestRating" content="5">
              <meta itemprop="worstRating" content="1">
            </div>
            <template v-if="'reviewList' in product.metaData">
              <div
                v-for="review in product.metaData.reviewList"
                :key="review.id"
                itemscope
                itemprop="review"
                itemtype="https://schema.org/Review"
              >
                <meta itemprop="itemReviewed" :content="review.productName">
                <meta itemprop="datePublished" :content="review.date">
                <meta itemprop="reviewBody" :content="review.text">
                <span
                  itemscope
                  itemprop="author"
                  itemtype="https://schema.org/Person"
                >
                  <meta itemprop="name" :content="review.userName">
                </span>
                <div
                  class="oneReview"
                  itemprop="reviewRating"
                  itemscope
                  itemtype="https://schema.org/Rating"
                >
                  <span
                    itemscope
                    itemprop="author"
                    itemtype="https://schema.org/Person"
                  >
                    <meta itemprop="name" :content="review.userName">
                  </span>
                  <meta itemprop="ratingValue" :content="review.ratingValue">
                  <meta itemprop="worstRating" content="1">
                  <meta itemprop="bestRating" content="5">
                </div>
              </div>
            </template>
          </template>
        </template>
      </div>
      <!-- Meta Adatok END -->
      <div v-if="isProductLoading || product.isVisible">
        <div class="d-flex">
          <div
            class="col-12 col-md-7 col-xl-8 product-desc p-0 pr-md-4"
            :class="{
              'loading': isProductLoading,
            }"
          >
            <!-- Product image -->
            <div>
              <b-skeleton-wrapper :loading="isProductLoading">
                <!-- Product image loading skeleton -->
                <template #loading>
                  <div class="w-100 h-100 skeleton-div">
                    <b-skeleton-img class="skeleton-image" no-aspect animation="fade" />
                  </div>
                </template>
                <!-- Product image loaded -->
                <product-image
                  v-if="!isProductLoading"
                  :image-list="product.imageList"
                  :product-name="product.name"
                  :is-loading="isProductLoading"
                />
              </b-skeleton-wrapper>
            </div>
            <!-- Product image END -->
            <!-- Product details skeleton -->
            <div>
              <b-skeleton-wrapper v-if="isMobile" :loading="isProductLoading">
                <template #loading>
                  <!-- Név feletti link -->
                  <b-skeleton animation="fade" class="skeleton-text-link-size w-25" />
                  <!-- Termék neve -->
                  <div class="mt-2">
                    <b-skeleton animation="fade" class="skeleton-text-name-size" />
                    <b-skeleton animation="fade" class="skeleton-text-name-size" />
                  </div>
                  <!-- Ár -->
                  <div class="my-2">
                    <b-skeleton animation="fade" class="skeleton-text-price-size w-25" />
                  </div>
                  <!-- Egységár -->
                  <div class="my-2">
                    <b-skeleton animation="fade" class="skeleton-text-mass-price-size w-25" />
                  </div>
                  <!-- Flag szövegek (Nincs raktáron...) -->
                  <div class="my-2">
                    <b-skeleton animation="fade" class="skeleton-text-link-size w-25" />
                    <b-skeleton animation="fade" class="skeleton-text-link-size w-50" />
                  </div>
                  <!-- Íz, darab... selectek -->
                  <div class="mb-4">
                    <b-skeleton animation="fade" class="skeleton-select w-100 mb-4" />
                  </div>
                  <div class="mb-4">
                    <b-skeleton animation="fade" class="skeleton-select w-100 mb-4" />
                  </div>
                  <!-- További termékek -->
                  <div class="mt-4">
                    <b-skeleton-img animation="fade" height="388px" />
                  </div>
                </template>
                <!-- Product add to cart component -->
                <product-add-to-cart
                  v-if="!isProductLoading"
                  class="mb-3"
                  :product="product"
                  :is-other-component-selects-valid="isOtherComponentSelectsValid"
                  :selected-color="selectedColor"
                  :selected-size="selectedSize"
                  :selected-flavor="selectedFlavor"
                  :quantity="quantity"
                  :is-assembly-checked="isAssemblyChecked"
                  :current-price-formatted="currentPriceFormatted"
                  :dosen-price-list="product.dosenPrices"
                  :is-product-add-to-cart-in-progress="isProductAddToCartInProgress"
                  @flavorSelected="flavorSelected"
                  @colorSelected="colorSelected"
                  @sizeSelected="sizeSelected"
                  @quantityIncreased="quantityIncreased"
                  @quantityDecreased="quantityDecreased"
                  @quantitySelected="quantitySelected"
                  @addToCart="addToCart"
                  @assemblyChanged="assemblyChanged"
                  @productCardAddToCart="productCardAddToCart"
                  @updateGiftProduct="updateGiftProduct"
                  @updateProductPackageItem="updateProductPackageItem"
                  @isValidChanged="isValidChanged"
                />
                <!-- Product add to cart component END -->
              </b-skeleton-wrapper>
            </div>
            <!-- Product details skeleton END -->
            <template v-if="isProductLoading || !product.isAdsFreeProduct">
              <!-- Vevővélemény skeleton -->
              <div>
                <b-skeleton-wrapper :loading="isProductLoading">
                  <template #loading>
                    <b-skeleton animation="fade" class="title mt-4 skeleton" />
                  </template>
                  <h2 v-t="'term_reszletes_velemenyek'" class="title mt-4" />
                </b-skeleton-wrapper>
              </div>
              <!-- Vevővélemény skeleton END -->
              <!-- Rating stars -->
              <div class="my-4">
                <b-skeleton-wrapper :loading="isProductLoading">
                  <!-- Rating stars skeleton -->
                  <template #loading>
                    <b-skeleton animation="fade" class="w-25 skeleton-rating-text" />
                  </template>
                  <!-- Rating stars skeleton END -->
                  <rating-stars
                    v-if="!isProductLoading"
                    :rating="product.rateValue"
                    :review-count="product.reviewCount"
                    :is-rate-num-visible="true"
                  />
                </b-skeleton-wrapper>
              </div>
              <!-- Rating stars END -->
              <!-- Review -->
              <div>
                <b-skeleton-wrapper :loading="isProductLoading">
                  <!-- Review skeleton-->
                  <template #loading>
                    <div class="skeleton-review-wrapper">
                      <b-skeleton animation="fade" class="w-100 skeleton-normal-text" />
                    </div>
                  </template>
                  <!-- Review skeleton END-->
                  <template v-if="!isProductLoading">
                    <review-bar
                      v-for="reviewBar in product.reviewBarData"
                      :key="reviewBar.starCount"
                      :star-count="reviewBar.starCount"
                      :percent="reviewBar.percent"
                      :is-loading="isProductLoading"
                      class="my-2"
                    />
                  </template>
                </b-skeleton-wrapper>
              </div>
              <!-- Review END -->
              <!-- Vélemény és termék kérdés -->
              <div class="d-flex flex-column align-items-start w-100">
                <!-- Vélemény és termék kérdés skeleton -->
                <b-skeleton-wrapper class="w-100" :loading="isProductLoading">
                  <template #loading>
                    <b-skeleton class="skeleton-normal-text w-25" />
                    <b-skeleton class="skeleton-question-btn w-25" />
                  </template>
                  <template v-if="!isProductLoading">
                    <router-link :to="product.reviewsUrl">
                      <span
                        v-t="'menu_shop_osszes_velemeny'"
                        class="reviews-text my-4"
                        data-cy="all-ratings-btn"
                      /> ({{ product.reviewCount }})
                    </router-link>
                    <router-link :to="product.questionsUrl">
                      <button
                        v-t="'kerdes_termekrol'"
                        class="custom-button unfilled my-4"
                        data-cy="product-question-btn"
                      />
                    </router-link>
                  </template>
                </b-skeleton-wrapper>
              </div>
              <!-- Vélemény és termék kérdés END-->
            </template>
            <!-- Product leírás -->
            <div v-observe-visibility="productDescriptionVisibilityChanged">
              <template v-if="isProductDescriptionVisible">
                <b-skeleton-wrapper class="w-100" :loading="isProductLoading">
                  <!-- Product leírás skeleton -->
                  <template #loading>
                    <div class="skeleton-title-wrapper">
                      <b-skeleton animation="fade" class="w-50 skeleton-title" />
                    </div>
                    <div v-for="index in 10" :key="index" class="mt-4 mb-4">
                      <b-skeleton animation="fade" class="w-50" />
                      <b-skeleton animation="fade" class="w-100" />
                      <b-skeleton animation="fade" class="w-75" />
                      <b-skeleton animation="fade" class="w-25" />
                    </div>
                  </template>
                  <!-- Product leírás skeleton END-->
                  <template v-if="!isProductLoading">
                    <div
                      v-if="product.isVirtualProduct && product.hasAccessToVirtualProduct && product.hasDigitalContent"
                      v-html="product.digitalContent"
                    />
                    <template v-else>
                      <h2 v-t="'term_termekleiras_label'" class="title" />
                      <text-and-widget-display
                        v-if="product.description !== ''"
                        class="description-body my-4"
                        :text-body="product.description"
                      />
                      <div
                        v-else
                        v-t="'term_nincstermekleiras_label'"
                        class="text-center my-4"
                      />
                      <text-and-widget-display
                        v-if="product.videoAdWidget !== ''"
                        class="description-body"
                        :text-body="product.videoAdWidget"
                      />
                      <div v-if="product.isVirtualProduct && product.hasDigitalContent">
                        <div v-html="$t('digital_content_information')" />
                        <template v-if="!isLoggedIn">
                          <div v-t="'digital_content_not_registered'" />
                          <router-link v-t="'bejelentkezes_kerese'" to="/login" />
                        </template>
                        <template v-else-if="!product.hasAccessToVirtualProduct">
                          <div v-t="'digital_content_not_ordered'" />
                        </template>
                      </div>
                    </template>
                  </template>
                </b-skeleton-wrapper>
              </template>
            </div>
            <!-- Product leírás END -->
            <!-- Product összetétel -->
            <div v-if="isProductDescriptionVisible">
              <b-skeleton-wrapper :loading="isProductLoading">
                <!-- Termék összetétel skeleton -->
                <template #loading>
                  <div class="skeleton-title-wrapper">
                    <b-skeleton animation="fade" class="w-50 skeleton-title" />
                  </div>
                  <b-skeleton class="composition-skeleton" animation="fade" />
                </template>
                <!-- Termék összetétel skeleton END -->
                <div v-if="(product && !product.isAdsFreeProduct && compositionCount > 0) && !isProductLoading">
                  <h2 v-if="currentCompositionsToShow.length !== 0" v-t="'term_reszletes_osszetetel'" class="title" />
                  <div class="w-100">
                    <template v-if="currentCompositionsToShow.length !== 0">
                      <composition
                        v-for="(compositionData, compositionIndex) in currentCompositionsToShow"
                        :key="compositionIndex"
                        :composition-data="compositionData"
                        class="mt-4"
                      />
                    </template>
                    <div class="my-4">
                      <router-link
                        v-if="compositionCount > currentCompositionsToShow.length"
                        :to="product.compositionsUrl"
                      >
                        <span v-t="'more_compositions'" class="compositions-text" />
                      </router-link>
                    </div>
                  </div>
                </div>
              </b-skeleton-wrapper>
            </div>
            <!-- Product összetétel END -->
          </div>
          <!-- Product description -->
          <div
            :class="{
              'right-bar-loading': isProductLoading,
            }"
          >
            <b-skeleton-wrapper v-if="!isMobile" :loading="isProductLoading">
              <template #loading>
                <!-- Név feletti link -->
                <b-skeleton animation="fade" class="skeleton-text-link-size w-25" />
                <!-- Termék neve -->
                <div class="mt-2">
                  <b-skeleton animation="fade" class="skeleton-text-name-size" />
                  <b-skeleton animation="fade" class="skeleton-text-name-size" />
                </div>
                <!-- Ár -->
                <div class="my-2">
                  <b-skeleton animation="fade" class="skeleton-text-price-size w-25" />
                </div>
                <!-- Egységár -->
                <div class="my-2">
                  <b-skeleton animation="fade" class="skeleton-text-mass-price-size w-25" />
                </div>
                <!-- Flag szövegek (Nincs raktáron...) -->
                <div class="my-2">
                  <b-skeleton animation="fade" class="skeleton-text-link-size w-25" />
                  <b-skeleton animation="fade" class="skeleton-text-link-size w-50" />
                </div>
                <!-- Íz, darab... selectek -->
                <div class="mb-4">
                  <b-skeleton animation="fade" class="skeleton-select w-100 mb-4" />
                </div>
                <div class="mb-4">
                  <b-skeleton animation="fade" class="skeleton-select w-100 mb-4" />
                </div>
                <!-- További termékek -->
                <div class="mt-4">
                  <b-skeleton-img animation="fade" height="388px" />
                </div>
              </template>
              <product-add-to-cart
                v-if="!isProductLoading"
                :product="product"
                :is-other-component-selects-valid="isOtherComponentSelectsValid"
                :selected-color="selectedColor"
                :selected-size="selectedSize"
                :selected-flavor="selectedFlavor"
                :quantity="quantity"
                :is-assembly-checked="isAssemblyChecked"
                :current-price-formatted="currentPriceFormatted"
                :dosen-price-list="product.dosenPrices"
                :is-product-add-to-cart-in-progress="isProductAddToCartInProgress"
                @flavorSelected="flavorSelected"
                @colorSelected="colorSelected"
                @sizeSelected="sizeSelected"
                @quantityIncreased="quantityIncreased"
                @quantityDecreased="quantityDecreased"
                @quantitySelected="quantitySelected"
                @addToCart="addToCart"
                @assemblyChanged="assemblyChanged"
                @productCardAddToCart="productCardAddToCart"
                @updateGiftProduct="updateGiftProduct"
                @updateProductPackageItem="updateProductPackageItem"
                @isValidChanged="isValidChanged"
              />
            </b-skeleton-wrapper>
          </div>
          <!-- Product description END-->
        </div>
        <template v-if="product !== null && !product.isAdsFreeProduct">
          <!-- Candy bar -->
          <div>
            <b-skeleton-wrapper :loading="isProductLoading">
              <!-- Candy bar skeleton -->
              <template #loading>
                <div class="d-flex justify-content-center mt-5">
                  <b-skeleton class="w-25 py-3" />
                </div>
                <div class="mt-4 mb-2 d-flex justify-content-center">
                  <candy-bar
                    :is-loading="true"
                    :product-list="[]"
                    class="col-11"
                  />
                </div>
              </template>
              <!-- Candy bar skeleton END-->
              <candy-bar
                v-if="product.similarItemList.length > 0 && !isProductLoading"
                :title="$t('hasonlo_termekek')"
                :product-list="product.similarItemList"
                class="mt-2 mb-4 px-xl-4"
              />
            </b-skeleton-wrapper>
          </div>
          <!-- Candy bar END -->
          <!-- Articles -->
          <div v-if="product.relevantArticleList.length > 0">
            <div
              v-t="'kapcsolodo_cikkek_label'"
              class="article-bar-title my-3 col-12 text-center"
            />
            <div class="d-flex flex-wrap">
              <article-card
                v-for="article in product.relevantArticleList"
                :key="article.id"
                :article="article"
                :is-overlay-type="true"
                class="col-12 col-md-6 my-2"
                :is-loading="isProductLoading"
              />
            </div>
          </div>
          <!-- Articles END -->
        </template>
      </div>
      <!-- Product nem visible szöveg -->
      <div v-else>
        <div class="text-center">
          <h1>
            {{ product.name }} - {{ product.brandName }}
          </h1>
          <div v-t="'nem_lathato_termekoldal_leiras'" class="my-3" />
          <h2
            v-if="'imgSrc' in product.replaceData && product.replaceData.imgSrc !== ''"
            v-t="'nem_lathato_termekoldal_magyarazat'"
            class="mb-3"
          />
        </div>
        <router-link
          v-if="'imgSrc' in product.replaceData && product.replaceData.imgSrc !== ''"
          :to="product.replaceData.url"
          :title="product.replaceData.name"
        >
          <img
            :src="product.replaceData.imgSrc"
            class="w-100"
          >
        </router-link>
        <candy-bar
          v-if="'replaceItemList' in product.replaceData && product.replaceData.replaceItemList.length > 0"
          :title="$t('replace_products_label')"
          :product-list="product.replaceData.replaceItemList"
          class="mt-2 mb-4 px-xl-4"
        />
        <div v-if="'otherProductsInCategoryItemList' in product.replaceData && product.replaceData.otherProductsInCategoryItemList.length > 0">
          <div
            v-t="'replace_products_list_label'"
            class="item-list-title col-12 text-center my-3"
          />
          <item-list-items
            :items="product.replaceData.otherProductsInCategoryItemList"
          />
        </div>
        <candy-bar
          v-if="'recommendedItemList' in product.replaceData && product.replaceData.recommendedItemList.length > 0"
          :title="$t('neked_ajanljuk')"
          :product-list="product.replaceData.recommendedItemList"
          class="mt-2 mb-4 px-xl-4"
        />
      </div>
      <!-- Product nem visible szöveg END -->
    </div>
    <!-- Product null Error betöltésnél -->
    <div v-else class="d-flex justify-content-center align-items-center error-container">
      <h4 v-t="'sikertelen_muvelet'" />
    </div>
    <!-- Product null Error betöltésnél END -->
    <add-to-cart-modal
      v-if="currentProductIdForAddToCartModal !== null"
      :product-id="currentProductIdForAddToCartModal"
      @close="currentProductIdForAddToCartModal = null"
    />
  </div>
</template>

<script>
import Axios from 'axios'
import { NOTY_TYPE } from '../../constant/builderConsts.js'
import { mapActions, mapGetters } from 'vuex'
import { BSkeleton, BSkeletonImg, BSkeletonWrapper } from 'bootstrap-vue'
import Vue from 'vue'
import { ObserveVisibility } from 'vue-observe-visibility'

Vue.directive('observe-visibility', ObserveVisibility)

export default {
  name: 'ProductPage',
  components: {
    'add-to-cart-modal': () => import(/* webpackChunkName: "AddToCartModal" */ '../../components/AddToCartModal/AddToCartModal.vue'),
    'composition': () => import(/* webpackChunkName: "composition" */ '../../components/composition/composition.vue'),
    'product-image': () => import(/* webpackChunkName: "productImage" */ '../../components/productImage/productImage.vue'),
    'product-add-to-cart': () => import(/* webpackChunkName: "productAddToCart" */ '../../components/productAddToCart/productAddToCart.vue'),
    'review-bar': () => import(/* webpackChunkName: "reviewBar" */ '../../components/reviewBar/reviewBar.vue'),
    'rating-stars': () => import(/* webpackChunkName: "RatingStars" */ '../../components/RatingStars/RatingStars.vue'),
    'text-and-widget-display': () => import(/* webpackChunkName: "textAndWidgetDisplay" */ '../../components/textAndWidgetDisplay/textAndWidgetDisplay.vue'),
    'candy-bar': () => import(/* webpackChunkName: "CandyBar" */ '../../components/CandyBar/CandyBar.vue'),
    'article-card': () => import(/* webpackChunkName: "ArticleCard" */ '../../components/ArticleCard/ArticleCard.vue'),
    'item-list-items': () => import(/* webpackChunkName: "itemListItems" */ '../../components/item-list-items/item-list-items.vue'),
    'b-skeleton-wrapper': BSkeletonWrapper,
    'b-skeleton-img': BSkeletonImg,
    'b-skeleton': BSkeleton,
  },
  data() {
    return {
      /**
       * termék kosárhoz adás modalhoz a választott termék id
       */
      currentProductIdForAddToCartModal: null,
      /**
       * termék object
       *
       * @type {{
       *   brandName: String,
       *   brandUrl: String,
       *   compositionList: {
       *     base: [{
       *       calorie: String,
       *       calorieFat: String,
       *       tableDataList: [
       *         carb: {
       *           dv: String,
       *           value: String,
       *         },
       *         carbNet: {
       *           dv: String,
       *           value: String,
       *         },
       *         colesterin: {
       *           dv: String,
       *           value: String,
       *         },
       *         fat: {
       *           dv: String,
       *           value: String,
       *         },
       *         fiber: {
       *           dv: String,
       *           value: String,
       *         },
       *         fullFat: {
       *           dv: String,
       *           value: String,
       *         },
       *         fullFatMultiple: {
       *           dv: String,
       *           value: String,
       *         },
       *         fullFatSingle: {
       *           dv: String,
       *           value: String,
       *         },
       *         kalium: {
       *           dv: String,
       *           value: String,
       *         },
       *         natrium: {
       *           dv: String,
       *           value: String,
       *         },
       *         protein: {
       *           dv: String,
       *           value: String,
       *         },
       *         sugar: {
       *           dv: String,
       *           value: String,
       *         },
       *         sugarAlcohol: {
       *           dv: String,
       *           value: String,
       *         },
       *         transFat: {
       *           dv: String,
       *           value: String,
       *         },
       *       ],
       *       comment: String,
       *       description: String,
       *       ingredientList: [
       *         {
       *           amount: String,
       *           name: String,
       *           rda: String,
       *         },
       *       ],
       *       isCalorieCheck: Boolean,
       *       name: String,
       *       nameWithoutBrand: String,
       *       serving: String,
       *       servingSize: String,
       *     },],
       *     variant: [{
       *       calorie: String,
       *       calorieFat: String,
       *       tableDataList: [
       *         carb: {
       *           dv: String,
       *           value: String,
       *         },
       *         carbNet: {
       *           dv: String,
       *           value: String,
       *         },
       *         colesterin: {
       *           dv: String,
       *           value: String,
       *         },
       *         fat: {
       *           dv: String,
       *           value: String,
       *         },
       *         fiber: {
       *           dv: String,
       *           value: String,
       *         },
       *         fullFat: {
       *           dv: String,
       *           value: String,
       *         },
       *         fullFatMultiple: {
       *           dv: String,
       *           value: String,
       *         },
       *         fullFatSingle: {
       *           dv: String,
       *           value: String,
       *         },
       *         kalium: {
       *           dv: String,
       *           value: String,
       *         },
       *         natrium: {
       *           dv: String,
       *           value: String,
       *         },
       *         protein: {
       *           dv: String,
       *           value: String,
       *         },
       *         sugar: {
       *           dv: String,
       *           value: String,
       *         },
       *         sugarAlcohol: {
       *           dv: String,
       *           value: String,
       *         },
       *         transFat: {
       *           dv: String,
       *           value: String,
       *         },
       *       ],
       *       comment: String,
       *       description: String,
       *       ingredientList: [
       *         {
       *           amount: String,
       *           name: String,
       *           rda: String,
       *         },
       *       ],
       *       isCalorieCheck: Boolean,
       *       name: String,
       *       nameWithoutBrand: String,
       *       serving: String,
       *       servingSize: String,
       *     },],
       *   },
       *   description: String,
       *   id: Number,
       *   imageList: [
       *     String,
       *   ],
       *   isDiscounted: Boolean,
       *   isOrderable: Boolean,
       *   discountValueFormatted: String,
       *   discountValueInSecondaryFormatted: String|null,
       *   discountPercent: Number,
       *   isVisible: Boolean,
       *   name: String,
       *   videoAdWidget: String,
       *   oldPrice: String,
       *   oldPriceSecondaryFormatted: String|null,
       *   similarItemList: Array,
       *   relevantArticleList: Array,
       *   replaceData: Array,
       *   paramVariantList: {
       *     flavors: [
       *       {
       *         id: String,
       *         code: String,
       *         isDefault: Boolean,
       *         label: String,
       *         fullImageSrc: String,
       *         gtinId: String,
       *         imageSrc: String,
       *         isVariantImage: boolean,
       *         sku: String,
       *         selectableList: [
       *           color: [
       *             String,
       *           ],
       *           flavor: [
       *             String,
       *           ],
       *           size: [
       *             String,
       *           ],
       *         ]
       *       },
       *     ],
       *     colors: [
       *       {
       *         id: String,
       *         code: String,
       *         isDefault: Boolean,
       *         label: String,
       *         fullImageSrc: String,
       *         gtinId: String,
       *         imageSrc: String,
       *         isVariantImage: boolean,
       *         sku: String,
       *         selectableList: [
       *           color: [
       *             String,
       *           ],
       *           flavor: [
       *             String,
       *           ],
       *           size: [
       *             String,
       *           ],
       *         ]
       *       },
       *     ],
       *     sizes: [
       *       {
       *         id: String,
       *         code: String,
       *         isDefault: Boolean,
       *         label: String,
       *         fullImageSrc: String,
       *         gtinId: String,
       *         imageSrc: String,
       *         isVariantImage: boolean,
       *         sku: String,
       *         priceFormatted: String,
       *         priceSecondaryFormatted: String|null,
       *         selectableList: [
       *           color: [
       *             String,
       *           ],
       *           flavor: [
       *             String,
       *           ],
       *           size: [
       *             String,
       *           ],
       *         ]
       *       },
       *     ],
       *   }|null,
       *   point: Number,
       *   price: String,
       *   loginPriceSecondaryFormatted: String|null,
       *   priceSecondaryFormatted: String|null,
       *   questionsUrl: String,
       *   rateValue: Number,
       *   reviewBarData: [{
       *     starCount: Number,
       *     percent: Number,
       *   },],
       *   reviewCount: Number,
       *   reviewsUrl: String,
       *   compositionsUrl: String,
       *   selectedColor: String|null,
       *   selectedFlavor: String|null,
       *   selectedSize: String|null,
       *   unitPriceFormatted: String|null,
       *   loginPriceFormatted: String|null,
       *   loginUnitPriceFormatted: String|null,
       *   hasSpecialShippingFlag: Boolean,
       *   specialShippingPriceFormatted: String|null,
       *   isPriceBySizeType: Boolean,
       *   isPriceDosenType: Boolean,
       *   isItemPackage: Boolean,
       *   packageComponentProductList: Array,
       *   otherPackagingItemList: Array,
       *   giftProductList: Array,
       *   dataLayerItemData: Array,
       *   dosenPrices: [
       *     {
       *       quantity: Number,
       *       priceFormatted: String,
       *       priceSecondaryFormatted: String|null,
       *     },
       *   ],
       *   hasAssemblyFlag: Boolean,
       *   isAdsFreeProduct: Boolean,
       *   initialVariantId: number,
       *   assemblyPriceFormatted: String|null,
       *   stockInfo: {
       *     inStock: Number,
       *     msg: String,
       *   }|null,
       *   metaData: {
       *     reviewList: [{
       *       id: Number,
       *       productName: String,
       *       date: String,
       *       text: String,
       *       userName: String,
       *       ratingValue: String,
       *     },],
       *     product: {
       *       sku: String,
       *       gtin: String,
       *       name: String,
       *       category: String,
       *       description: String,
       *       image: String,
       *       offers: {
       *         url: String,
       *         priceCurrency: String,
       *         price: String,
       *         availability: String,
       *         priceValidUntil: String,
       *       },
       *       brand: {
       *         logo: String,
       *         name: String,
       *         url: String,
       *       },
       *       aggregateRating: {
       *         ratingValue: Number,
       *         reviewCount: Number,
       *         itemReviewedType: String,
       *       }|undefined,
       *     },
       *   }
       * }|null}
       */
      product: null,
      /**
       * kosárba adás folyamatban van-e, ha igen disabled a gomb
       *
       * @type {boolean}
       */
      isProductAddToCartInProgress: false,
      /**
       * termék betöltése folyamatban van-e
       */
      isProductLoading: false,
      /**
       * termékhez választott íz
       */
      selectedFlavor: null,
      /**
       * termékhez választott szín
       */
      selectedColor: null,
      /**
       * termékhez választott méret
       */
      selectedSize: null,
      /**
       * termék mennyisége
       */
      quantity: 1,
      /**
       * termék leírás és widgetek láthatók-e
       *
       * @type {Boolean}
       */
      isProductDescriptionVisible: false,
      /**
       * termék szerelés kérése be van-e pipálva
       */
      isAssemblyChecked: false,
      giftProductList: [],
      productPackageItemList: [],
      paramVariantList: null,
      isOtherComponentSelectsValid: true,
      /**
       * termék saját param selectjei validak-e
       *
       * @type {boolean}
       */
      isProductSelectsValid: true,
      /**
       * összetételek száma
       *
       * @type {number}
       */
      compositionCount: 0,
      currentCompositionsToShow: [],
    }
  },
  computed: {
    currentPriceFormatted() {
      if (this.product.isPriceBySizeType) {
        const currentSizeIndex = this.product.paramVariantList.sizes.findIndex(size => size.code === this.selectedSize)
        if (currentSizeIndex !== -1) {
          let priceFormatted = this.product.paramVariantList.sizes[currentSizeIndex].priceFormatted
          if (this.product.paramVariantList.sizes[currentSizeIndex].priceSecondaryFormatted !== null) {
            priceFormatted += ' (' + this.product.paramVariantList.sizes[currentSizeIndex].priceSecondaryFormatted + ')'
          }
          return priceFormatted
        }
      } else if (this.product.isPriceDosenType) {
        const dosenPrice = this.product.dosenPrices.filter(dosenPrice => dosenPrice.quantity <= this.quantity)
          .reduce((dosenPriceFirst, dosenPriceSecond) => {
            return dosenPriceFirst.quantity > dosenPriceSecond.quantity ? dosenPriceFirst : dosenPriceSecond
          })
        let priceFormatted = dosenPrice.priceFormatted
        if (dosenPrice.priceSecondaryFormatted !== null) {
          priceFormatted += ' (' + dosenPrice.priceSecondaryFormatted + ')'
        }
        return priceFormatted
      }
      let priceFormatted = this.product.price
      if (this.product.priceSecondaryFormatted !== null) {
        priceFormatted += ' (' + this.product.priceSecondaryFormatted + ')'
      }
      return priceFormatted
    },
    /**
     * mobil nézet-e
     *
     * @return {boolean}
     */
    isMobile() {
      return this.$mq === 'sm' || this.$mq === 'md'
    },
    ...mapGetters({
      isLoggedIn: 'User/isLoggedIn',
    }),
  },
  created() {
    this.loadProduct()
  },
  methods: {
    ...mapActions({
      getBasicCheckoutData: 'Cart/getBasicCheckoutData',
      getCartAddressData: 'Cart/getCartAddressData',
      getCartEditData: 'Cart/getCartEditData',
    }),
    /**
     * megjelenítendő composition lista
     * ha van variant id, akkor nem package a termék, csak azt mutatjuk amit az alapján látni kell
     * ha nincs, akkor package, és a csomagban lévő termékek összetételét mutatjuk, ami az aktiv variant alapján kell látszódjon
     *
     * @param variantId
     */
    setCurrentComposition(variantId = null) {
      if (this.product.isItemPackage) {
        this.currentCompositionsToShow = []
        const addedCompositionIdList = []
        const addedVariantIdList = {}
        this.productPackageItemList.forEach(selectedComponentVariant => {
          let currentComposition = null
          if (addedVariantIdList[selectedComponentVariant.id] === undefined || !addedVariantIdList[selectedComponentVariant.id].includes(selectedComponentVariant.variantId)) {
            if (selectedComponentVariant.variantId !== null && selectedComponentVariant.variantId in this.product.compositionList.package[selectedComponentVariant.id].variant) {
              currentComposition = this.product.compositionList.package[selectedComponentVariant.id].variant[selectedComponentVariant.variantId]
            } else if (this.product.compositionList.package[selectedComponentVariant.id].base.length !== 0) {
              currentComposition = this.product.compositionList.package[selectedComponentVariant.id].base[0]
            }
            if (addedVariantIdList[selectedComponentVariant.id] === undefined) {
              addedVariantIdList[selectedComponentVariant.id] = [selectedComponentVariant.variantId]
            } else {
              addedVariantIdList[selectedComponentVariant.id].push(selectedComponentVariant.variantId)
            }
            if (currentComposition !== null && !addedCompositionIdList.includes(currentComposition.id)) {
              addedCompositionIdList.push(currentComposition.id)
              this.currentCompositionsToShow.push(currentComposition)
            }
          }
        })
      } else {
        if (variantId !== null && variantId in this.product.compositionList.variant) {
          this.currentCompositionsToShow = [this.product.compositionList.variant[variantId]]
        } else if (this.product.compositionList.base.length !== 0) {
          this.currentCompositionsToShow = [this.product.compositionList.base[0]]
        } else {
          this.currentCompositionsToShow = []
        }
      }
    },
    /**
     * termék leírás és widgetek megvannak-e jelenitve
     *
     * @param isVisible
     */
    productDescriptionVisibilityChanged(isVisible) {
      if (this.isProductDescriptionVisible || !isVisible) {
        return
      }
      this.isProductDescriptionVisible = true
    },
    /**
     * termék csomag komponens selectjei valid-e
     */
    refreshIsOtherComponentSelectsValid() {
      this.isOtherComponentSelectsValid = this.giftProductList.findIndex(giftItem => !giftItem.isValid) === -1 &&
        this.productPackageItemList.findIndex(packageItem => !packageItem.isValid) === -1
    },
    /**
     * termékhez választott paramok valid-e
     */
    isValidChanged(newIsValid) {
      this.isProductSelectsValid = newIsValid
    },
    /**
     * termék csomag résztermék update
     */
    updateProductPackageItem(productPackageItem) {
      const productPackageItemIndex = this.productPackageItemList.findIndex(packageItem => packageItem.uniqueId === productPackageItem.uniqueId)
      if (productPackageItemIndex === -1) {
        this.productPackageItemList.push(productPackageItem)
      } else {
        this.productPackageItemList[productPackageItemIndex] = productPackageItem
      }
      this.setCurrentComposition()
      this.refreshIsOtherComponentSelectsValid()
    },
    /**
     * ajándék termék update
     */
    updateGiftProduct(giftProduct) {
      const giftProductIndex = this.giftProductList.findIndex(gift => gift.id === giftProduct.id)
      if (giftProductIndex === -1) {
        this.giftProductList.push(giftProduct)
      } else {
        this.giftProductList[giftProductIndex] = giftProduct
      }
      this.refreshIsOtherComponentSelectsValid()
    },
    /**
     * termék paraméter változott, ha van neki variant képe, azt mutatjuk,
     * ha nincs de van másik paraméter kiválasztva neküönk, aminek van akkor azt
     * ha az sincs akkor a default
     */
    changeProductImageByVariant(type) {
      let selectedValue = null
      let paramList = null
      if (type === 'flavor') {
        selectedValue = this.selectedFlavor
        paramList = this.paramVariantList.flavors
      } else if (type === 'size') {
        selectedValue = this.selectedSize
        paramList = this.paramVariantList.sizes
      } else if (type === 'color') {
        selectedValue = this.selectedColor
        paramList = this.paramVariantList.colors
      }
      if (selectedValue === null || paramList === null) {
        return
      }
      const selectedIndex = paramList.findIndex(variant => variant.code === selectedValue)
      if (selectedIndex === -1) {
        return
      }
      const currentVariant = paramList[selectedIndex]
      this.setCurrentComposition(currentVariant.id)
      this.$set(this.product.metaData.product, 'gtin', currentVariant.gtinId)
      this.$set(this.product.metaData.product, 'sku', currentVariant.sku)
      if (currentVariant.isVariantImage) {
        this.$set(this.product.imageList, 0, currentVariant.imageSrc)
        this.$set(this.product.metaData.product, 'image', currentVariant.fullImageSrc)
        // amit váltottunk annak van variáns képe, végeztünk
        return
      }
      // váltottnak nem volt variáns képe, nézzük van-e olyan selected paramunk amihez van
      if (this.selectedFlavor !== null) {
        const currentFlavor = this.paramVariantList.flavors.find(flavor => flavor.code === this.selectedFlavor)
        if (currentFlavor !== undefined) {
          this.$set(this.product.metaData.product, 'gtin', currentFlavor.gtinId)
          this.$set(this.product.metaData.product, 'sku', currentFlavor.sku)
          if (currentFlavor.isVariantImage) {
            this.$set(this.product.imageList, 0, currentFlavor.imageSrc)
            this.$set(this.product.metaData.product, 'image', currentFlavor.fullImageSrc)
            // választott iznek volt variáns képe, végeztünk
            return
          }
        }
      } else if (this.selectedColor !== null) {
        const currentColor = this.paramVariantList.colors.find(color => color.code === this.selectedColor)
        if (currentColor !== undefined) {
          this.$set(this.product.metaData.product, 'gtin', currentColor.gtinId)
          this.$set(this.product.metaData.product, 'sku', currentColor.sku)
          if (currentColor.isVariantImage) {
            this.$set(this.product.imageList, 0, currentColor.imageSrc)
            this.$set(this.product.metaData.product, 'image', currentColor.fullImageSrc)
            // választott szinnek volt variáns képe, végeztünk
            return
          }
        }
      } else if (this.selectedSize !== null) {
        const currentSize = this.paramVariantList.sizes.find(size => size.code === this.selectedSize)
        if (currentSize !== undefined) {
          this.$set(this.product.metaData.product, 'gtin', currentSize.gtinId)
          this.$set(this.product.metaData.product, 'sku', currentSize.sku)
          if (currentSize.isVariantImage) {
            this.$set(this.product.imageList, 0, currentSize.imageSrc)
            this.$set(this.product.metaData.product, 'image', currentSize.fullImageSrc)
            // választott méretnek volt variáns képe, végeztünk
            return
          }
        }
      }
      // egyiknek sem volt variáns képe, visszaváltjuk a defaultra
      this.$set(this.product.metaData.product, 'gtin', currentVariant.gtinId)
      this.$set(this.product.metaData.product, 'sku', currentVariant.sku)
      this.$set(this.product.imageList, 0, currentVariant.imageSrc)
      this.$set(this.product.metaData.product, 'image', currentVariant.fullImageSrc)
    },
    /**
     * iz változtatása
     *
     * @param value
     */
    flavorSelected(value) {
      this.selectedFlavor = value
      this.refreshIsInStock()
      this.changeProductImageByVariant('flavor')
    },
    /**
     * szin változtatása
     *
     * @param value
     */
    colorSelected(value) {
      this.selectedColor = value
      this.refreshIsInStock()
      this.changeProductImageByVariant('color')
    },
    /**
     * meret változtatása
     *
     * @param value
     */
    sizeSelected(value) {
      this.selectedSize = value
      const currentSize = this.paramVariantList.sizes.find(variant => variant.code === this.selectedSize)
      if ('productName' in currentSize) {
        this.$set(this.product, 'name', currentSize.productName)
      }
      if ('unitPriceFormatted' in currentSize) {
        this.$set(this.product, 'unitPriceFormatted', currentSize.unitPriceFormatted)
      }
      if ('loginUnitPriceFormatted' in currentSize) {
        this.$set(this.product, 'loginUnitPriceFormatted', currentSize.loginUnitPriceFormatted)
      }
      this.refreshIsInStock()
      this.changeProductImageByVariant('size')
    },
    /**
     * mennyiség növelése
     *
     * @param value
     */
    quantityIncreased() {
      if (this.quantity < 99) {
        this.quantity++
      }
    },
    /**
     * mennyiség csökkentése
     *
     * @param value
     */
    quantityDecreased() {
      if (this.quantity > 1) {
        this.quantity--
      }
    },
    /**
     * mennyiség csökkentése
     *
     * @param value
     */
    quantitySelected(value) {
      this.quantity = value
    },
    /**
     * szerelés kell vagy nem változtatása
     *
     * @param newVal
     */
    assemblyChanged(newVal) {
      this.isAssemblyChecked = newVal
    },
    /**
     * kis kártyás addtocart -> add to cart modal felnyitása
     */
    productCardAddToCart(product) {
      this.currentProductIdForAddToCartModal = product.id
    },
    /**
     * termék kosárhoz adása, ajándékkal, csomaggal, szereléssel, stb
     */
    addToCart() {
      if (this.isProductAddToCartInProgress) {
        return
      }
      this.isProductAddToCartInProgress = true
      Axios.post('/Shop/api/addItemToCartFromItemPage', {
        id: this.product.id,
        flavor: this.selectedFlavor,
        color: this.selectedColor,
        size: this.selectedSize,
        quantity: this.quantity,
        isAssemblyChecked: this.isAssemblyChecked,
        giftProductList: this.giftProductList,
        productPackageItemList: this.productPackageItemList,
      })
        .then(({ data }) => {
          this.getBasicCheckoutData(true)
          this.getCartAddressData(true)
          this.getCartEditData(true)
            .then(() => {
              this.isProductAddToCartInProgress = false
              this.$root.$emit('notificationPush', this.$t('kosarba_kerult'), NOTY_TYPE.NOTY_SUCCESS)
              this.emarsysCartChangedEvent()
              window.dataLayerAddItemToCart(data)
            })
        })
        .catch((e) => {
          this.isProductAddToCartInProgress = false
          console.error('addToCart', e)
          this.$root.$emit('notificationPush', this.$t('sikertelen_muvelet'), NOTY_TYPE.NOTY_ERROR)
        })
    },
    /**
     * jelenlegi paraméterekkel van-e raktáron a termék lekérése
     */
    refreshIsInStock() {
      Axios.get('/Shop/api/isVariantInStock', {
        params: {
          itemId: this.product.id,
          flavor: this.selectedFlavor,
          color: this.selectedColor,
          size: this.selectedSize,
        },
      })
        .then(({ data }) => {
          if (this.isProductSelectsValid) {
            this.$set(this.product, 'stockInfo', data)
          } else {
            // ha olyan param van kiválasztva ami eleve X-es, akkor nem akarjuk a usernek mutatni a stock infot, mert ugysem rendelheti meg
            this.$set(this.product, 'stockInfo', null)
          }
          // a metat akkor is updateljük
          this.$set(this.product.metaData.product.offers, 'availability', data.offerAvailability)
        })
        .catch((e) => {
          console.error('refreshIsInStock', e)
        })
    },
    /**
     * termék adatok betöltése
     */
    loadProduct() {
      this.isProductLoading = true
      Axios.get('Product/api/getProductData', {
        params: {
          id: this.$route.params.id,
          pid: this.$route.query.pid,
          gtin: this.$route.query.gtin,
        },
      })
        .then(({ data }) => {
          this.product = data
          this.quantity = 1
          this.productPackageItemList = []
          this.giftProductList = []
          document.title = this.product.title
          this.paramVariantList = this.product.paramVariantList
          this.selectedFlavor = this.product.selectedFlavor
          this.selectedColor = this.product.selectedColor
          this.selectedSize = this.product.selectedSize
          if (this.product.isItemPackage) {
            const countedComponentItemIdList = []
            if (this.product.compositionList.package.length > 0) {
              this.product.packageComponentProductList.forEach(packageComponent => {
                if (!countedComponentItemIdList.includes(packageComponent.id)) {
                  countedComponentItemIdList.push(packageComponent.id)
                  this.compositionCount +=
                      this.product.compositionList.package[packageComponent.id].base.length + Object.keys(this.product.compositionList.package[packageComponent.id].variant).length
                }
              })
            }
          } else {
            this.compositionCount = this.product.compositionList.base.length + Object.keys(this.product.compositionList.variant).length
            this.setCurrentComposition(this.product.initialVariantId)
          }
          window.dataLayerViewItem(this.product.dataLayerItemData)
          window.addEmarsys(this.product.emarsys)
          window.addRemarketing(this.product.remarketing)
        })
        .catch((e) => {
          console.error(e)
          this.product = null
          this.paramVariantList = null
          this.selectedFlavor = null
          this.selectedColor = null
          this.selectedSize = null
        })
        .finally(() => {
          this.isProductLoading = false
        })
    },
  },
}
</script>

<style scoped lang="scss">
.article-bar-title {
  font-size: 32px;
  line-height: 48px;
  font-weight: 800;
  color: #111;
  text-transform: uppercase;

  @media screen and (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
}

.product-desc {
  max-width: 736px;

  &.loading {
    width: 100%;
  }
}

.right-bar-loading {
  overflow-x: hidden;
}

.composition-skeleton {
  height: 700px;
  width: 375px;

  @media only screen and (max-width: 576px) {
    width: 95%;
  }
}

.product {
  max-width: 352px;
}

.title {
  color: #111;
  font-weight: 800;
  font-size: 32px;
  line-height: 48px;
  text-transform: uppercase;

  @media screen and (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }

  &.skeleton {
    height: 48px;
  }
}

.description-body {
  @media screen and (max-width: 768px) {
    max-width: 86vw;
  }
}

.reviews-text,
.compositions-text {
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  text-decoration: underline;

  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
}

.ingredient-disclaimer {
  max-width: 374px;
}

.item-list-title {
  font-size: 32px;
  line-height: 48px;
  font-weight: 800;
  color: #111;
  text-transform: uppercase;

  @media screen and (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
}

.skeleton-div {
  margin-bottom: 16px;
  max-height: 720px;
}

::v-deep .b-skeleton-img {
  aspect-ratio: 1.175;
  width: 100%;
  padding: 0 40px 0 20px;
  margin: 10px 0;
}

.skeleton-normal-text {
  height: 24px;
}

.skeleton-question-btn {
  height: 90px;
}
.skeleton-title-wrapper {
  margin-bottom: 8px;

  .skeleton-title {
    height: 48px;
  }
}
.skeleton-rating-text {
  height: 16px;
}

.skeleton-review-wrapper {
  margin: 8px 0;
  padding: 15px 5px;
}

.skeleton-text-link-size {
  height: 17px;
}

.skeleton-text-name-size {
  height: 40px;
}

.skeleton-text-price-size {
  height: 24px;
}

.skeleton-text-mass-price-size {
  height: 16px;
}

.skeleton-select {
  height: 58px;
}

.skeleton-button {
  height: 42px;
}

.error-container {
  height: 90vh;
}
</style>

<style lang="scss">
.carousel-control-prev-icon, .carousel-control-next-icon {
  border: 1px solid #fff;
  border-radius: 20px;
  padding: 15px;
  background-size: 50%;
  background-color: black;
}
@media screen and (max-width: 768px) {
  .carousel-control-prev-icon, .carousel-control-next-icon {
    border: 1px solid #fff;
    border-radius: 20px;
    padding: 15px;
    background-size: 50%;
    background-color: black;
  }
}
</style>